/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { RightSheet } from "@lemmonhq/uikit";
import type { GiftCard, GiftCardTransaction } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  redeemed: "Redeemed",
  expired: "Expired",
  void: "Void",
};

const STATUS_BADGE: Record<string, string> = {
  active: "badge-success",
  redeemed: "badge-neutral",
  expired: "badge-warning",
  void: "badge-error",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-CH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

interface GiftCardDetailSheetProps {
  card: GiftCard | null;
  onClose: () => void;
  onVoid: (id: string) => Promise<void>;
}

export const GiftCardDetailSheet = defineComponent<GiftCardDetailSheetProps>({
  name: "GiftCardDetailSheet",
  props: ["card", "onClose", "onVoid"],
  setup(props) {
    const auth = useAuth();
    const voiding = ref(false);

    const handleVoid = async () => {
      if (!props.card) return;
      if (
        !confirm(
          "Are you sure you want to void this gift card? This cannot be undone.",
        )
      )
        return;
      voiding.value = true;
      try {
        await props.onVoid(props.card.id);
        props.onClose();
      } finally {
        voiding.value = false;
      }
    };

    return () => (
      <RightSheet
        isOpen={!!props.card}
        title="Gift Card"
        onClose={props.onClose}
        width="440px"
      >
        {props.card && (
          <div class="flex flex-col h-full">
            <div class="p-6 space-y-6 flex-1">
              {/* Card info */}
              <div class="bg-base-200 rounded-xl p-4 space-y-3">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-base-content/60">Code</span>
                  <span class="font-mono text-sm break-all text-right max-w-[260px]">
                    {props.card.code}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-base-content/60">Status</span>
                  <span
                    class={`badge ${STATUS_BADGE[props.card.status] || "badge-neutral"}`}
                  >
                    {STATUS_LABELS[props.card.status] || props.card.status}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-base-content/60">
                    Initial Value
                  </span>
                  <span class="font-semibold">
                    {auth.user.value?.restaurant.currency || "CHF"}{" "}
                    {Number(props.card.initial_value).toFixed(2)}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-base-content/60">
                    Current Balance
                  </span>
                  <span class="font-bold text-success text-xl">
                    {auth.user.value?.restaurant.currency || "CHF"}{" "}
                    {Number(props.card.current_balance).toFixed(2)}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-base-content/60">Issued</span>
                  <span class="text-sm">
                    {formatDate(props.card.created_at)}
                  </span>
                </div>
                {props.card.order_id && (
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-base-content/60">Order #</span>
                    <span class="text-sm">{props.card.order_id}</span>
                  </div>
                )}
              </div>

              {/* Transaction history */}
              {props.card.transactions &&
                props.card.transactions.length > 0 && (
                  <div class="space-y-2">
                    <h3 class="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
                      Transaction History
                    </h3>
                    <div class="space-y-2">
                      {props.card.transactions.map(
                        (tx: GiftCardTransaction) => (
                          <div
                            key={tx.id}
                            class="flex justify-between items-center bg-base-200 rounded-lg px-4 py-3"
                          >
                            <div>
                              <div class="text-sm font-medium capitalize">
                                {tx.type}
                              </div>
                              <div class="text-xs text-base-content/40">
                                {formatDate(tx.created_at)}
                              </div>
                            </div>
                            <div class="text-right">
                              <div
                                class={`font-semibold ${
                                  tx.type === "load" || tx.type === "refund"
                                    ? "text-success"
                                    : "text-base-content"
                                }`}
                              >
                                {tx.type === "load" || tx.type === "refund"
                                  ? "+"
                                  : "-"}
                                {auth.user.value?.restaurant.currency || "CHF"}{" "}
                                {Number(tx.amount).toFixed(2)}
                              </div>
                              <div class="text-xs text-base-content/40">
                                Balance:{" "}
                                {auth.user.value?.restaurant.currency || "CHF"}{" "}
                                {Number(tx.balance_after).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Void action */}
            {props.card.status === "active" && (
              <div class="px-6 py-4 border-t border-base-300">
                <button
                  class="btn btn-error btn-outline w-full"
                  disabled={voiding.value}
                  onClick={handleVoid}
                >
                  {voiding.value && (
                    <span class="loading loading-spinner loading-sm mr-2" />
                  )}
                  Void Gift Card
                </button>
              </div>
            )}
          </div>
        )}
      </RightSheet>
    );
  },
});
